import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, BarChart3, Calendar, Target, Database, BarChart, Zap } from 'lucide-react';

const LOADING_STEPS = [
  { icon: Database, label: 'Connecting to Salesforce...', delay: 0 },
  { icon: BarChart, label: 'Querying GA attribution data...', delay: 3000 },
  { icon: Zap, label: 'Aggregating campaign performance...', delay: 8000 },
  { icon: BarChart3, label: 'Building breakdowns...', delay: 15000 },
];

const LoadingAnimation = () => {
  const [step, setStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(prev => prev + 500);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    for (let i = LOADING_STEPS.length - 1; i >= 0; i--) {
      if (elapsed >= LOADING_STEPS[i].delay) {
        setStep(i);
        break;
      }
    }
  }, [elapsed]);

  const currentStep = LOADING_STEPS[step];
  const Icon = currentStep.icon;

  return (
    <div className="hf-map-card" style={{ padding: '2.5rem 2rem', textAlign: 'center' }}>
      <style>{`
        @keyframes sfPulse {
          0%, 100% { opacity: 0.4; transform: scaleX(0.3); }
          50% { opacity: 1; transform: scaleX(1); }
        }
        @keyframes sfSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes sfFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '48px', height: '48px', borderRadius: '50%',
          background: 'rgba(129, 140, 248, 0.15)', border: '1px solid rgba(129, 140, 248, 0.3)',
          animation: 'sfSpin 2s linear infinite',
        }}>
          <Icon size={22} style={{ color: '#818CF8' }} />
        </div>
      </div>
      <div style={{
        color: '#fff', fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem',
        animation: 'sfFadeIn 0.3s ease-out',
      }} key={step}>
        {currentStep.label}
      </div>
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', margin: '1rem 0 0.75rem' }}>
        {LOADING_STEPS.map((_, i) => (
          <div key={i} style={{
            width: '32px', height: '3px', borderRadius: '2px',
            background: i <= step ? '#818CF8' : 'rgba(129, 140, 248, 0.15)',
            transition: 'background 0.3s ease',
          }} />
        ))}
      </div>
      <div style={{
        width: '200px', height: '2px', margin: '0 auto',
        background: 'rgba(129, 140, 248, 0.1)', borderRadius: '1px', overflow: 'hidden',
      }}>
        <div style={{
          width: '100%', height: '100%',
          background: 'linear-gradient(90deg, transparent, #818CF8, transparent)',
          animation: 'sfPulse 1.5s ease-in-out infinite',
          transformOrigin: 'center',
        }} />
      </div>
      <div style={{ color: 'rgba(199, 210, 254, 0.35)', fontSize: '0.7rem', marginTop: '0.75rem' }}>
        {elapsed < 10000 ? 'This may take a moment — querying 3 years of data' : `Still working... ${Math.floor(elapsed / 1000)}s`}
      </div>
    </div>
  );
};

const SFMarketingAttribution = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dimension, setDimension] = useState('campaign');
  const [sortBy, setSortBy] = useState('inquiries');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/.netlify/functions/sf-marketing-attribution');
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const result = await res.json();
      setData(result);
    } catch (err) {
      console.error('Failed to fetch SF marketing attribution:', err);
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="hf-container">
        <LoadingAnimation />
      </div>
    );
  }

  if (error) {
    return (
      <div className="hf-map-card" style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ color: '#EF4444', marginBottom: '1rem', fontSize: '1.1rem' }}>Failed to load Salesforce data</div>
        <div style={{ color: 'rgba(199, 210, 254, 0.6)', marginBottom: '1rem' }}>{error}</div>
        <button onClick={fetchData} className="hf-zoom-out-btn" style={{ position: 'relative' }}>
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="hf-map-card" style={{ textAlign: 'center', padding: '2rem', color: 'rgba(199, 210, 254, 0.5)' }}>
        No marketing attribution data found.
      </div>
    );
  }

  // Date range for display
  const now = new Date();
  const threeYearsAgo = new Date(now);
  threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
  const dateRangeLabel = `${threeYearsAgo.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} — ${now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;

  // Get rows for selected dimension
  const dimensionData = dimension === 'campaign' ? data.byCampaign : dimension === 'medium' ? data.byMedium : data.bySource;
  const sortKey = sortBy;
  const isDaySort = sortBy === 'medianDaysToApply' || sortBy === 'medianDaysToEnroll';
  const rows = [...dimensionData].sort((a, b) => {
    if (isDaySort) {
      // nulls go last, lower days = better = first
      if (a[sortKey] === null && b[sortKey] === null) return 0;
      if (a[sortKey] === null) return 1;
      if (b[sortKey] === null) return -1;
      return a[sortKey] - b[sortKey];
    }
    return b[sortKey] - a[sortKey];
  });

  const dimensionLabel = dimension === 'campaign' ? 'GA Campaign' : dimension === 'medium' ? 'GA Medium' : 'GA Source';
  const maxMonthly = Math.max(...(data.monthlyTrend || []).map(m => Math.max(m.inquiries, m.applied, m.enrolled)), 1);

  // Top 5 per dimension (by enrolled, min 5 inquiries)
  const top5 = (dimData) => [...dimData]
    .filter(r => r.inquiries >= 5 && r.name !== '(not set)')
    .sort((a, b) => b.enrolled - a.enrolled)
    .slice(0, 5);

  return (
    <div className="hf-container">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>
            Salesforce Marketing Attribution
          </h2>
          <p style={{ color: 'rgba(199, 210, 254, 0.7)', fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>
            GA Campaign / Medium / Source performance through the enrollment pipeline
          </p>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.2rem' }}>
            <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.5)', background: 'rgba(129, 140, 248, 0.1)', padding: '0.15rem 0.5rem', borderRadius: '3px', border: '1px solid rgba(129, 140, 248, 0.15)' }}>
              Data range: {dateRangeLabel}
            </span>
            {data.fetchedAt && (
              <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.35)' }}>
                Last synced: {new Date(data.fetchedAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <button onClick={fetchData} style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          background: 'rgba(0, 190, 168, 0.15)', border: '1px solid rgba(0, 190, 168, 0.3)',
          color: '#4ADE80', borderRadius: '6px', padding: '0.4rem 0.75rem',
          fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Overall KPIs */}
      <div className="hf-kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #818CF8' }}>
          <div className="hf-kpi-value" style={{ color: '#818CF8' }}>{data.totalInquiries.toLocaleString()}</div>
          <div className="hf-kpi-label">Total Inquiries</div>
          <div className="hf-kpi-sub">With GA tracking data</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #00BEA8' }}>
          <div className="hf-kpi-value" style={{ color: '#00BEA8' }}>{data.totalApplied.toLocaleString()}</div>
          <div className="hf-kpi-label">Applied</div>
          <div className="hf-kpi-sub">{(data.totalApplied / data.totalInquiries * 100).toFixed(1)}% of inquiries</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #4ADE80' }}>
          <div className="hf-kpi-value" style={{ color: '#4ADE80' }}>{data.totalEnrolled.toLocaleString()}</div>
          <div className="hf-kpi-label">Enrolled</div>
          <div className="hf-kpi-sub">{(data.totalEnrolled / data.totalInquiries * 100).toFixed(1)}% of inquiries</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #FBBF24' }}>
          <div className="hf-kpi-value" style={{ color: '#FBBF24' }}>
            {data.totalApplied > 0 ? `${(data.totalEnrolled / data.totalApplied * 100).toFixed(1)}%` : '—'}
          </div>
          <div className="hf-kpi-label">Apply-to-Enroll Rate</div>
          <div className="hf-kpi-sub">Overall conversion</div>
        </div>
      </div>

      {/* Dimension Selector + Sort */}
      <div className="hf-map-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 className="hf-section-title" style={{ margin: 0 }}>
            <BarChart3 size={18} />
            Performance by {dimensionLabel}
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {['campaign', 'medium', 'source'].map(d => (
              <button
                key={d}
                onClick={() => setDimension(d)}
                style={{
                  padding: '0.3rem 0.6rem', fontSize: '0.72rem', borderRadius: '4px', cursor: 'pointer',
                  border: dimension === d ? '1px solid #818CF8' : '1px solid rgba(255,255,255,0.1)',
                  background: dimension === d ? 'rgba(129, 140, 248, 0.2)' : 'transparent',
                  color: dimension === d ? '#818CF8' : 'rgba(199, 210, 254, 0.6)',
                }}
              >
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Sort options */}
        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem', fontSize: '0.68rem', color: 'rgba(199, 210, 254, 0.5)' }}>
          <span style={{ marginRight: '0.25rem' }}>Sort by:</span>
          {[
            { key: 'inquiries', label: 'Inquiries' },
            { key: 'applied', label: 'Applied' },
            { key: 'enrolled', label: 'Enrolled' },
            { key: 'applyRate', label: 'Apply Rate' },
            { key: 'enrollRate', label: 'Enroll Rate' },
            { key: 'medianDaysToApply', label: 'Days to Apply' },
            { key: 'medianDaysToEnroll', label: 'Days to Enroll' },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setSortBy(s.key)}
              style={{
                padding: '0.2rem 0.45rem', fontSize: '0.68rem', borderRadius: '3px', cursor: 'pointer',
                border: 'none',
                background: sortBy === s.key ? 'rgba(0, 190, 168, 0.2)' : 'transparent',
                color: sortBy === s.key ? '#00BEA8' : 'rgba(199, 210, 254, 0.5)',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div style={{ fontSize: '0.8rem', maxHeight: '500px', overflowY: 'auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 80px 80px 80px 85px 85px 85px 80px 80px',
            padding: '0.5rem 0.75rem',
            color: 'rgba(199, 210, 254, 0.5)',
            fontWeight: 600,
            fontSize: '0.65rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            position: 'sticky',
            top: 0,
            background: 'rgba(15, 23, 42, 0.95)',
          }}>
            <span>{dimensionLabel}</span>
            <span style={{ textAlign: 'center' }}>Inquiries</span>
            <span style={{ textAlign: 'center' }}>Applied</span>
            <span style={{ textAlign: 'center' }}>Enrolled</span>
            <span style={{ textAlign: 'center' }}>Apply %</span>
            <span style={{ textAlign: 'center' }}>Enroll %</span>
            <span style={{ textAlign: 'center' }}>Overall %</span>
            <span style={{ textAlign: 'center' }}>Days to Apply</span>
            <span style={{ textAlign: 'center' }}>Days to Enroll</span>
          </div>
          {rows
            .filter(r => r.name !== '(not set)')
            .concat(rows.filter(r => r.name === '(not set)'))
            .map((row) => (
            <div key={row.name} style={{
              display: 'grid',
              gridTemplateColumns: '2fr 80px 80px 80px 85px 85px 85px 80px 80px',
              padding: '0.45rem 0.75rem',
              borderBottom: '1px solid rgba(255,255,255,0.03)',
              alignItems: 'center',
            }}>
              <span style={{
                color: row.name === '(not set)' ? 'rgba(199, 210, 254, 0.4)' : 'rgba(199, 210, 254, 0.9)',
                fontSize: '0.78rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }} title={row.name}>
                {row.name}
              </span>
              <span style={{ textAlign: 'center', color: '#818CF8' }}>{row.inquiries}</span>
              <span style={{ textAlign: 'center', color: '#00BEA8' }}>{row.applied}</span>
              <span style={{ textAlign: 'center', color: '#4ADE80' }}>{row.enrolled}</span>
              <span style={{
                textAlign: 'center', fontWeight: 700,
                color: row.applyRate > 30 ? '#4ADE80' : row.applyRate > 15 ? '#FBBF24' : '#EF4444',
              }}>
                {row.applyRate.toFixed(1)}%
              </span>
              <span style={{
                textAlign: 'center', fontWeight: 700,
                color: row.enrollRate > 50 ? '#4ADE80' : row.enrollRate > 25 ? '#FBBF24' : '#EF4444',
              }}>
                {row.applied > 0 ? `${row.enrollRate.toFixed(1)}%` : '—'}
              </span>
              <span style={{
                textAlign: 'center', fontWeight: 700,
                color: row.overallRate > 20 ? '#4ADE80' : row.overallRate > 8 ? '#FBBF24' : 'rgba(199, 210, 254, 0.5)',
              }}>
                {row.overallRate.toFixed(1)}%
              </span>
              <span style={{ textAlign: 'center', color: 'rgba(199, 210, 254, 0.6)', fontSize: '0.75rem' }}>
                {row.medianDaysToApply !== null ? `${row.medianDaysToApply}d` : '—'}
              </span>
              <span style={{ textAlign: 'center', color: 'rgba(199, 210, 254, 0.6)', fontSize: '0.75rem' }}>
                {row.medianDaysToEnroll !== null ? `${row.medianDaysToEnroll}d` : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Performers Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        {[
          { key: 'campaign', label: 'GA Campaign', rows: data.byCampaign },
          { key: 'medium', label: 'GA Medium', rows: data.byMedium },
          { key: 'source', label: 'GA Source', rows: data.bySource },
        ].map(({ key, label, rows: dimRows }) => {
          const topRows = top5(dimRows);
          return (
            <div key={key} className="hf-map-card">
              <h3 className="hf-section-title" style={{ fontSize: '0.85rem' }}>
                <Target size={16} />
                Top {label}s
              </h3>
              <div style={{ fontSize: '0.75rem' }}>
                {topRows.map((r, i) => (
                  <div key={r.name} style={{
                    padding: '0.4rem 0',
                    borderBottom: '1px solid rgba(255,255,255,0.03)',
                  }}>
                    <div style={{
                      color: 'rgba(199, 210, 254, 0.9)',
                      fontSize: '0.76rem',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: '0.2rem',
                    }} title={r.name}>
                      {i + 1}. {r.name}
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.7rem' }}>
                      <span><span style={{ color: '#818CF8' }}>{r.inquiries}</span> inq</span>
                      <span><span style={{ color: '#00BEA8' }}>{r.applied}</span> app</span>
                      <span><span style={{ color: '#4ADE80' }}>{r.enrolled}</span> enr</span>
                      <span style={{ color: r.applyRate > 25 ? '#4ADE80' : '#FBBF24' }}>{r.applyRate.toFixed(0)}%</span>
                    </div>
                  </div>
                ))}
                {topRows.length === 0 && (
                  <div style={{ color: 'rgba(199, 210, 254, 0.4)', padding: '0.5rem 0' }}>No data with 5+ inquiries</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Monthly Trend */}
      {data.monthlyTrend && data.monthlyTrend.length > 0 && (
        <div className="hf-map-card">
          <h3 className="hf-section-title">
            <Calendar size={18} />
            Monthly Trend — Inquiries vs Applications vs Enrollments (GA-tracked)
          </h3>
          <div style={{ padding: '0.5rem 0' }}>
            {data.monthlyTrend.map((month) => (
              <div key={month.month} style={{
                display: 'grid',
                gridTemplateColumns: '70px 1fr 1fr 1fr 60px',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.3rem 0',
                borderBottom: '1px solid rgba(255,255,255,0.03)',
              }}>
                <span style={{ fontSize: '0.75rem', color: 'rgba(199, 210, 254, 0.6)' }}>{month.label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    height: '14px',
                    width: `${(month.inquiries / maxMonthly * 100)}%`,
                    background: 'linear-gradient(90deg, rgba(129, 140, 248, 0.4), rgba(129, 140, 248, 0.8))',
                    borderRadius: '3px',
                    minWidth: month.inquiries > 0 ? '4px' : '0',
                  }} />
                  <span style={{ fontSize: '0.7rem', color: '#818CF8', whiteSpace: 'nowrap' }}>{month.inquiries}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    height: '14px',
                    width: `${(month.applied / maxMonthly * 100)}%`,
                    background: 'linear-gradient(90deg, rgba(0, 190, 168, 0.4), rgba(0, 190, 168, 0.8))',
                    borderRadius: '3px',
                    minWidth: month.applied > 0 ? '4px' : '0',
                  }} />
                  <span style={{ fontSize: '0.7rem', color: '#00BEA8', whiteSpace: 'nowrap' }}>{month.applied}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    height: '14px',
                    width: `${(month.enrolled / maxMonthly * 100)}%`,
                    background: 'linear-gradient(90deg, rgba(74, 222, 128, 0.4), rgba(74, 222, 128, 0.8))',
                    borderRadius: '3px',
                    minWidth: month.enrolled > 0 ? '4px' : '0',
                  }} />
                  <span style={{ fontSize: '0.7rem', color: '#4ADE80', whiteSpace: 'nowrap' }}>{month.enrolled}</span>
                </div>
                <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.4)', textAlign: 'right' }}>
                  {month.inquiries > 0 ? `${(month.applied / month.inquiries * 100).toFixed(0)}%` : '—'}
                </span>
              </div>
            ))}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '70px 1fr 1fr 1fr 60px',
              gap: '0.5rem',
              marginTop: '0.5rem',
              fontSize: '0.65rem',
              color: 'rgba(199, 210, 254, 0.4)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              <span></span>
              <span><span style={{ color: '#818CF8' }}>●</span> Inquiries</span>
              <span><span style={{ color: '#00BEA8' }}>●</span> Applied</span>
              <span><span style={{ color: '#4ADE80' }}>●</span> Enrolled</span>
              <span style={{ textAlign: 'right' }}>Conv%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SFMarketingAttribution;
